import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { mdiAlertCircleOutline, mdiChevronLeft } from "@mdi/js";
import Icon from "@mdi/react";
import { useToggle } from "usehooks-ts";

import WatchDemoModal from "components/WatchDemoModal";
import { NOTIFICATION_TIP } from "constants/link";
import { routes } from "constants/routes";
import { showSuccessMessage, showErrorMessage } from "helpers/toastMessage";
import { useUserFeature } from "hooks/useUserFeature";
import MainLayout from "layouts/Main";
import { ShipperSettingsData, UserSettingsData } from "models/UserSetting";
import {
  getShipperSettingsAPI,
  getUserSettingsAPI,
  sendVerificationEmailAPI
} from "services/api/userSetting";
import { AppDispatch, RootState } from "store";
import { handleLoading } from "store/global/loader";
import ActivitySettingsForm from "./ActivitySettingsForm";
import MessageSettingsForm from "./MessageSettingsForm";
import ShipperSettingsForm from "./ShipperSettingsForm";
import styles from "./style.module.scss";

const NotificationsHeader = ({ goBack }: { goBack: VoidFunction }) => {
  const [isTipModalOpen, toggleTipModal] = useToggle();

  return (
    <div className="flex items-center justify-between flex-1 gap-3">
      <div className="flex desktop:hidden">
        <button type="button" onClick={goBack}>
          <Icon path={mdiChevronLeft} className="size-8" />
        </button>
        <h4 className="text-2xl font-medium">Notifications</h4>
      </div>
      <button
        type="button"
        onClick={toggleTipModal}
        className="flex justify-end gap-2 underline desktop:!ml-auto desktop:mt-3">
        <Icon path={mdiAlertCircleOutline} />
        <span className="mobile:hidden">Pro Tip</span>
      </button>

      <WatchDemoModal
        open={isTipModalOpen}
        toggleModal={toggleTipModal}
        url={NOTIFICATION_TIP}
        title={"Pro Tip: Shipper Load Alerts"}
        content={`
          We recommend including as many states as possible in order to get the full spectrum of new available verified direct shipper loads so you don’t miss any potential newcustomers!
        `}
      />
    </div>
  );
};

export default function UserNotifications() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { userDetails } = useSelector((state: RootState) => state.userState);

  const { isShipper, isTextNotificationRole } = useUserFeature();
  const [userSettingsPermission, setUserSettingsPermission] = useState<UserSettingsData>();
  const [shipperSettingsPermission, setShipperSettingsPermission] = useState<ShipperSettingsData>();

  useEffect(() => {
    const fetchSettings = async () => {
      const settingPromises = [];
      settingPromises.push(getShipperSettingsAPI());
      settingPromises.push(getUserSettingsAPI());
      dispatch(handleLoading(true));
      const response = await Promise.all(settingPromises);
      dispatch(handleLoading(false));
      const shipperSettings = response[0].data;
      const userSettings = response[1].data;

      if (shipperSettings) {
        setShipperSettingsPermission(shipperSettings);
      }
      if (userSettings) {
        setUserSettingsPermission(userSettings);
      }
    };

    fetchSettings();
  }, []);

  const sendVerificationEmail = async () => {
    try {
      const response = await sendVerificationEmailAPI();
      if (response) {
        showSuccessMessage("Successfully sent email.");
      } else {
        showErrorMessage(
          "We’ve sent an email to your given email address. Please wait before requesting another."
        );
      }
    } catch (error: any) {
      showErrorMessage(error.message);
    }
  };

  const goToAccount = () => navigate(routes.SETTINGS_ACCOUNT);
  const goToUserSettings = () => navigate(routes.SETTINGS);

  return (
    <MainLayout
      desktopTitle="Notifications"
      customHeader={<NotificationsHeader goBack={goToUserSettings} />}
      hideNavigation
      className={styles.userNotificationsContainer}>
      {!userDetails?.has_verified_email && (
        <div className={styles.info}>
          <p>
            Please verify your email address by clicking the link in the Account Verification email
            that we sent to you. Once you verify your email address, you will be able to manage your
            push and email notifications here. You may need to check your spam folder. If you
            haven’t received it, please make sure your account email address is correct{" "}
            <button type="button" className="underline text-info" onClick={goToAccount}>
              here
            </button>{" "}
            or email{" "}
            <a href="mailto:support@comfreight.com" className="underline text-info">
              support@comfreight.com
            </a>
            {". "}You can{" "}
            <button className="text-info" onClick={sendVerificationEmail}>
              click here
            </button>{" "}
            to request another verification email.
          </p>
        </div>
      )}

      <div className={styles.body}>
        {!isShipper && shipperSettingsPermission && (
          <ShipperSettingsForm defaultValue={shipperSettingsPermission} />
        )}

        {userSettingsPermission && <ActivitySettingsForm defaultValue={userSettingsPermission} />}

        {isTextNotificationRole && <MessageSettingsForm />}
      </div>
    </MainLayout>
  );
}
